import React from "react";

import { FormattedMessage, FormattedDate } from "react-intl";

import { Button } from "@/components/atoms/Button";
import { Skeleton } from "@/components/atoms/Skeleton";
import { Text } from "@/components/atoms/Text";
import { buildSimilarSpacesUrl } from "~/components/listings/utils";
import { PaymentMethodMessagingElement } from "~/components/stripe";
import { useEnvData } from "~/context/EnvDataContext";
import { ListingData, useListingData } from "~/context/ListingPageDataContext";
import { useIsKlarnaSupported } from "~/hooks/use-is-klarna-supported";
import { IFaqs } from "~/types/api-types";

import { useBookListingPriceData } from "../book-listing-hooks";
import { getIsInstantBookOffered } from "../book-listing-utils";
import { useBookListingContext } from "../BookListingContext";
import { useZeroDownPayment } from "../hooks/use-zero-down-payment";
import HostResponsiveness from "../HostResponsiveness";
import { IdvWarning } from "../IdvWarning";
import { InstantBookNote } from "../InstantBookNote";

import { MobileBookListingButton } from "./MobileBookListingButton";
import { PriceDetailsButton } from "./PriceDetailsButton";

type MobileBookListingSectionProps = {
  faqs: IFaqs | undefined;
  pageTitle: string;
  guestNeedsVerification: boolean | undefined;
};

export const MobileBookListingSection = ({
  faqs,
  pageTitle,
  guestNeedsVerification,
}: MobileBookListingSectionProps) => {
  const { data: listingData = {} as ListingData } = useListingData();
  const env = useEnvData();
  const { quote, estimate, isFetchingQuote } = useBookListingContext();
  const { masterContract } = quote || {};
  const { calculations } = masterContract || {};

  const isListingPaused =
    listingData.listingStatus === "PAUSED" ||
    listingData.listingStatus === "PAUSED_INTERNALLY";

  const similarSpacesUrl = buildSimilarSpacesUrl({
    city: listingData.listingCity,
    state: listingData.listingState,
    useType: listingData.useType,
  });

  // compare current time with cancellation policy ts_active timestamp to show copy after effective date
  const currentUnixTime = new Date().valueOf();
  const showCancelCopy =
    !!listingData?.cancellationPolicy &&
    currentUnixTime >= listingData.cancellationPolicy.ts_active;

  const showPriceDetails =
    !!(quote || estimate || isFetchingQuote) &&
    (calculations?.guest?.payment ?? 0) > 0;

  const isKlarnaSupported = useIsKlarnaSupported();
  const { total, currency, country } = useBookListingPriceData();

  const { showZeroDownSkeleton, isZeroDownPayment } = useZeroDownPayment();

  const booking = quote?.spaceRentalContract?.meta?.booking;
  const dtsFullRefund = booking?.cancellation_policy?.dts_full_refund;

  const isInstantBookOffered = getIsInstantBookOffered({
    booking,
    listingData,
  });

  if (isListingPaused) {
    return (
      <div>
        <Text
          typeface="body-1"
          className="tw-text-gray-600 tw-text-center tw-mb-16"
        >
          <FormattedMessage defaultMessage="This space is currently unavailable for booking" />
        </Text>
        <Button
          appearance="purple"
          className="tw-w-full"
          href={similarSpacesUrl}
        >
          <FormattedMessage defaultMessage="Browse similar spaces" />
        </Button>
      </div>
    );
  }

  return (
    <div>
      {isKlarnaSupported && showPriceDetails && !isZeroDownPayment ? (
        <div className="tw-mb-16">
          <PaymentMethodMessagingElement
            amount={total}
            currency={currency}
            country={country}
            id="payment-method-messaging-element-mobile"
          />
        </div>
      ) : null}

      {isFetchingQuote && showZeroDownSkeleton ? (
        <div className="tw-text-center tw-text-gray-600 tw-mb-16">
          <Skeleton height={20} width={140} classNames="tw-mx-auto" />
        </div>
      ) : (
        isZeroDownPayment && (
          <div className="tw-flex tw-flex-col tw-items-center tw-text-center tw-mb-16">
            <Text typeface="body-1-m" className="tw-text-gray-900 tw-mb-4">
              <FormattedMessage defaultMessage="$0 due today" />
            </Text>
            {dtsFullRefund && (
              <Text typeface="body-1" className="tw-text-gray-600">
                <FormattedMessage
                  defaultMessage="Free cancellation before {date}"
                  values={{
                    date: (
                      <FormattedDate
                        value={dtsFullRefund}
                        month="short"
                        day="numeric"
                      />
                    ),
                  }}
                />
              </Text>
            )}
          </div>
        )
      )}

      <div className="tw-flex tw-gap-12">
        {showPriceDetails && (
          <PriceDetailsButton
            listingData={listingData}
            disabled={isFetchingQuote}
          />
        )}
        <MobileBookListingButton
          faqs={faqs}
          pageTitle={pageTitle}
          quote={quote}
        />
      </div>

      {isInstantBookOffered ? (
        <InstantBookNote
          displayInstantBook={isInstantBookOffered}
          isMinimumDuration={true}
          showNewCancelCopy={true}
        />
      ) : (
        !!listingData?.listingHostResponsiveness?.hours && (
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-8 tw-mt-8 tw-text-gray-600">
            <HostResponsiveness
              ownerName={listingData.listingOwnerFirstName}
              hostResponsiveness={listingData.listingHostResponsiveness}
              isFullServiceVenue={listingData.fullService?.active}
            />
            {showCancelCopy && (
              <div id="cancel-for-free-copy">
                <Text typeface="body-1-long">
                  <FormattedMessage defaultMessage="Cancel for free within 24 hours" />
                </Text>
              </div>
            )}
            {guestNeedsVerification && (
              <IdvWarning zendeskUrlLocale={env.currentLocaleData.localeCode} />
            )}
          </div>
        )
      )}
    </div>
  );
};
